<script setup lang="ts">
import { computed, onMounted, nextTick, watch } from 'vue';

interface Good {
    name: string;
    classNumber: number;
}

interface Props {
    classes: number[];
    goods: Good[];
    classNumberOnly?: boolean;
    deleteable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    classNumberOnly: false,
    deleteable: false
});

const stringifyGoods = (goods: Good[]): string => {
    const joined = goods.map(good => good.name).join('; ');
    return joined.charAt(0).toUpperCase() + joined.slice(1);
};

const goodsByClass = computed(() => {
    return [...props.classes]
        .sort((a, b) => a - b)
        .map(classNumber => {
            return {
                classNumber,
                goods: props.goods.filter(good => good.classNumber === classNumber)
            };
        });
});

const checkTruncation = async () => {
    await nextTick();
    const elements = document.querySelectorAll('.stf-ellipsis');
    elements.forEach(element => {
        const isTruncated = element.scrollWidth > element.clientWidth;
        element.setAttribute('data-truncated', isTruncated.toString());
    });
};

const getClassName = (classNumber: number) => {
    return props.classNumberOnly ? classNumber : `Class ${classNumber}`;
};

onMounted(async () => {
    await checkTruncation();
});

// Watch for changes in goods to recheck truncation
watch(() => props.goods, async () => {
    await checkTruncation();
}, { deep: true });

const emit = defineEmits<{
    (e: 'delete', classNumber: number): void
}>();
</script>

<template>
    <div v-for="item in goodsByClass" 
         :key="item.classNumber" 
         :data-class="item.classNumber"
         class="is-flex stf-nowrap is-clickable goods-row"
    >
        <StfIcon v-if="deleteable" icon="xMark" size="small" class="mr-2" @click="emit('delete', item.classNumber)"/>
        <b>{{ getClassName(item.classNumber) }}:</b>
        <span class="ml-2 is-flex-grow-1 stf-ellipsis">
            {{ stringifyGoods(item.goods) }}
        </span>
        <span class="toggle-arrow">⌵</span>
    </div>
</template>

<style scoped lang="scss">
@use '@/assets/sass/_variables' as vars;

.toggle-arrow {
  margin-left: 8px;
  color: #666;
  font-size: vars.$font-size-18;
  cursor: pointer;
  user-select: none;
  opacity: 0;
  transition: opacity 0.2s;
  line-height: .7;
  transform: scaleX(1.3);
  display: inline-block;
  font-weight: vars.$font-weight-bold;
}

/* Show arrow only when text is truncated */
.stf-ellipsis[data-truncated="true"] + .toggle-arrow {
  opacity: 1;
}

.goods-row:hover .toggle-arrow {
  opacity: 0;
}

.goods-row:hover .stf-ellipsis {
  white-space: normal;
  overflow: visible;
}
</style> 